import { CONST } from '@common/constants/const.constant';
import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import { createCollection } from '@common/utils/create-collection.util';
import { createCustomerDto, ICustomerDto } from '@src/api/model/customer.dto';
import { createReferralProgramDto, IReferralProgramDto } from '@src/api/model/reward.dto';
import {
    ICustomerDataUpdate,
    registrationApiRequest,
    TRegistrationV1CustomerGetError,
    TRegistrationV1CustomerPatchError,
    TRegistrationV1CustomerReferralProgramGetError,
} from '@src/api/registration-generated-api';
import { queryClient } from '@src/shared/query-client';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';

const NAME = 'customer';

const useGetCustomer = (
    queryOptions: UseQueryOptions,
    return_to?: string,
): UseQueryResult<ICustomerDto, IBEError<TRegistrationV1CustomerGetError>> => {
    return useQuery<ICustomerDto, IBEError<TRegistrationV1CustomerGetError>>(
        `${NAME}-id`,
        () => {
            return registrationApiRequest.registrationV1CustomerGet({ return_to }, {}).then((r) => createCustomerDto(r.data));
        },
        queryOptions,
    );
};

const useUpdateCustomer = (): UseMutationResult<
    ICustomerDto,
    IBEError<TRegistrationV1CustomerPatchError>,
    ICustomerDataUpdate
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1CustomerPatch(props, {}).then((r) => createCustomerDto(r.data));
        },
        {
            mutationKey: `${NAME}-update`,
            onSuccess: async (newData) => {
                updateCustomerCache(newData);
            },
        },
    );
};

const updateCustomerCache = (data: ICustomerDto): void => {
    queryClient.setQueryData(`${NAME}-id`, (): ICustomerDto => data);
};

const refetchCustomerCache = (): void => {
    queryClient.refetchQueries(`${NAME}-id`);
};

const useGetReferralProgram = (
    options?: UseQueryOptions,
): UseQueryResult<ICollection<IReferralProgramDto>, IBEError<TRegistrationV1CustomerReferralProgramGetError>> => {
    return useQuery<ICollection<IReferralProgramDto>, IBEError<TRegistrationV1CustomerReferralProgramGetError>>(
        `${NAME}-customer-referrals`,
        () => {
            return registrationApiRequest.registrationV1CustomerReferralProgramGet({}).then((res) => {
                return createCollection(
                    res.data.rewards?.map((elem) => {
                        return createReferralProgramDto(elem);
                    }),
                    0,
                    { limit: CONST.LIST_LIMIT },
                );
            });
        },
        { ...options },
    );
};

export const customer = {
    useGetReferralProgram,
    useGetCustomer,
    useUpdateCustomer,
    refetchCustomerCache,
};
